import { useEffect, useRef, useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  IDLE_TIMEOUT,
  MAX_SESSION_DURATION,
  WARNING_PERIOD,
  ACTIVITY_EVENTS,
  HEARTBEAT_INTERVAL,
} from '../config/sessionTimeout';
import { apiClient } from '../services/api/client';

export type TimeoutReason = 'idle' | 'max_duration';

interface UseSessionTimeoutOptions {
  onTimeout: (reason: TimeoutReason) => void;
  sessionStartTime: number | null;
  enabled: boolean;
}

export const useSessionTimeout = ({
  onTimeout,
  sessionStartTime,
  enabled,
}: UseSessionTimeoutOptions) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeoutReason, setTimeoutReason] = useState<TimeoutReason | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);

  const lastActivityRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxSessionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Store latest callbacks in refs to avoid stale closures
  const resetIdleTimerRef = useRef<() => void>();
  const updateActivityRef = useRef<() => void>();
  const onTimeoutRef = useRef(onTimeout);

  // Keep onTimeout ref up to date
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Clear countdown interval
  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Show warning modal with countdown
  const showWarningModal = useCallback(
    (reason: TimeoutReason, timeoutMs: number) => {
      logger.log(`[SessionTimeout] Showing warning modal: ${reason}, ${timeoutMs}ms until timeout`);
      setTimeoutReason(reason);
      setShowWarning(true);
      setSecondsRemaining(Math.ceil(timeoutMs / 1000));

      // Start countdown
      clearCountdown();
      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            clearCountdown();
            // When countdown reaches 0, hide the warning modal
            // The actual logout will happen via the timeout callback
            setShowWarning(false);
          }
          return Math.max(0, next);
        });
      }, 1000);
    },
    [clearCountdown]
  );

  // Reset idle timer
  const resetIdleTimer = useCallback(() => {
    // Clear existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Clear existing warning timer
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    if (!enabled || !sessionStartTime) return;

    const now = Date.now();
    const sessionAge = now - sessionStartTime;
    const timeUntilMaxSession = MAX_SESSION_DURATION * 1000 - sessionAge;

    // If we're already past max session, trigger immediately
    if (timeUntilMaxSession <= 0) {
      logger.log('[SessionTimeout] Max session duration exceeded');
      onTimeout('max_duration');
      return;
    }

    // Set idle timeout (converted to milliseconds)
    const idleTimeoutMs = IDLE_TIMEOUT * 1000;

    // Set warning timer for idle timeout (show warning 2 minutes before)
    const warningTimeMs = Math.max(0, idleTimeoutMs - WARNING_PERIOD);
    warningTimerRef.current = setTimeout(() => {
      showWarningModal('idle', WARNING_PERIOD);
    }, warningTimeMs);

    // Set idle timeout (use ref to get latest callback)
    idleTimerRef.current = setTimeout(() => {
      logger.log('[SessionTimeout] Idle timeout reached');
      onTimeoutRef.current('idle');
    }, idleTimeoutMs);

    logger.log(
      `[SessionTimeout] Idle timer reset: warning in ${Math.floor(warningTimeMs / 1000)}s, timeout in ${Math.floor(idleTimeoutMs / 1000)}s`
    );
  }, [enabled, sessionStartTime, onTimeout, showWarningModal]);

  // Set max session duration timer
  const setMaxSessionTimer = useCallback(() => {
    if (!enabled || !sessionStartTime) return;

    // Clear existing timer
    if (maxSessionTimerRef.current) {
      clearTimeout(maxSessionTimerRef.current);
    }

    const now = Date.now();
    const sessionAge = now - sessionStartTime;
    const timeUntilMaxSession = MAX_SESSION_DURATION * 1000 - sessionAge;

    // If we're already past max session, trigger immediately
    if (timeUntilMaxSession <= 0) {
      logger.log('[SessionTimeout] Max session duration exceeded at init');
      onTimeoutRef.current('max_duration');
      return;
    }

    // Set warning timer (show warning 2 minutes before max session)
    const warningTime = Math.max(0, timeUntilMaxSession - WARNING_PERIOD);
    if (warningTime > 0) {
      setTimeout(() => {
        // Only show max duration warning if idle warning isn't already showing
        if (!showWarning) {
          showWarningModal('max_duration', WARNING_PERIOD);
        }
      }, warningTime);
    }

    // Set max session timeout (use ref to get latest callback)
    maxSessionTimerRef.current = setTimeout(() => {
      logger.log('[SessionTimeout] Max session duration reached');
      onTimeoutRef.current('max_duration');
    }, timeUntilMaxSession);

    logger.log(
      `[SessionTimeout] Max session timer set: ${Math.floor(timeUntilMaxSession / 1000)}s remaining`
    );
  }, [enabled, sessionStartTime, onTimeout, showWarning, showWarningModal]);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    if (!enabled) return;

    // DON'T count activity while warning is showing
    // User must explicitly click "Continuar Sesión" to extend
    if (showWarning) {
      return;
    }

    const now = Date.now();
    lastActivityRef.current = now;

    // Reset idle timer using ref
    resetIdleTimerRef.current?.();
  }, [enabled, showWarning]);

  // Store latest callback in ref
  updateActivityRef.current = updateActivity;

  // Store resetIdleTimer in ref
  resetIdleTimerRef.current = resetIdleTimer;

  // Extend session (dismiss warning and reset idle timer)
  const extendSession = useCallback(() => {
    logger.log('[SessionTimeout] Session extended by user');
    setShowWarning(false);
    setTimeoutReason(null);
    clearCountdown();
    lastActivityRef.current = Date.now();
    resetIdleTimerRef.current?.();
  }, [clearCountdown]);

  // Initialize session timeout tracking (only when enabled or sessionStartTime changes)
  useEffect(() => {
    if (!enabled || !sessionStartTime) {
      logger.log('[SessionTimeout] Disabled or no session start time');
      return;
    }

    logger.log('[SessionTimeout] Initializing session timeout tracking');

    // Set max session timer
    setMaxSessionTimer();

    // Set idle timer
    resetIdleTimer();

    // Set up activity listeners (use ref to always get latest callback)
    const handleActivity = () => updateActivityRef.current?.();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      logger.log('[SessionTimeout] Cleaning up session timeout tracking');

      // Remove activity listeners
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Clear all timers
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (maxSessionTimerRef.current) clearTimeout(maxSessionTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      clearCountdown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionStartTime]);

  // Heartbeat: ping the backend periodically while the user is active.
  // Keeps server-side lastActivityAt alive during long frontend-only workflows
  // where no API calls are made for 30+ minutes (e.g. multi-trade booking modal).
  // Only fires when real DOM activity has occurred within the last HEARTBEAT_INTERVAL.
  useEffect(() => {
    if (!enabled || !sessionStartTime) return;

    const intervalId = setInterval(() => {
      // Don't ping while the warning modal is showing
      if (showWarning) return;

      // Only ping if the user has been genuinely active within this interval window.
      // lastActivityRef is updated by real DOM events (mousemove, click, keypress, etc.).
      // If the user is truly idle, this check fails and no heartbeat is sent.
      const userWasRecentlyActive = lastActivityRef.current > Date.now() - HEARTBEAT_INTERVAL;
      if (!userWasRecentlyActive) return;

      logger.log('[SessionTimeout] Sending session heartbeat');
      apiClient.get('/api/v1/auth/heartbeat').catch(() => {
        // Silently ignore — heartbeat failures must never disrupt the user
      });
    }, HEARTBEAT_INTERVAL);

    return () => clearInterval(intervalId);
  }, [enabled, sessionStartTime, showWarning]);

  // Hide warning modal (for manual logout)
  const hideWarning = useCallback(() => {
    setShowWarning(false);
    setTimeoutReason(null);
    clearCountdown();
  }, [clearCountdown]);

  return {
    showWarning,
    timeoutReason,
    secondsRemaining,
    extendSession,
    hideWarning,
  };
};
